import {
  NODE_SCHEMAS_BY_TYPE,
  YDOC_CREATION_ROOT_FIELDS,
  type JsonValue,
  type YDocWriteRecord,
} from '../generated/nodeDataTypes'
import { toBackendType } from '../utils/nodeTypes'
import { withVideoGeneratorCreationAudioDefaults } from '../utils/videoGeneratorAudioDefaults'

/** Return whether a schema default can be materialized as a nested Y.Map. */
function isObjectDefault(
  value: JsonValue | undefined,
): value is Record<string, JsonValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** Seed nested maps before collaborators fork so independent entry writes merge. */
export function prepareYDocCreationData(
  nodeType: string,
  data?: YDocWriteRecord,
): YDocWriteRecord | undefined {
  const normalized = withVideoGeneratorCreationAudioDefaults(nodeType, { ...data })
  // Legacy structural groups are intentionally outside generated node schemas.
  if (nodeType === 'group') {
    return Object.keys(normalized).length > 0 ? normalized : undefined
  }
  const backendType = toBackendType(nodeType)
  const fields = YDOC_CREATION_ROOT_FIELDS[backendType] ?? new Set<string>()
  const schema =
    NODE_SCHEMAS_BY_TYPE[nodeType] ??
    Object.values(NODE_SCHEMAS_BY_TYPE).find((item) => item.backendType === backendType)
  if (!schema) throw new Error(`Missing generated node schema for ${nodeType}`)
  // Imported/copied read projections may contain aliases. Only canonical fields
  // belong in a new node's persisted data, including for legacy stale exports.
  for (const output of schema.outputs) {
    if (output.aliasOf) delete normalized[output.id]
  }
  if (fields.size > 0) {
    for (const fieldName of fields) {
      if (normalized[fieldName] !== undefined) continue
      const defaultValue = schema.inputs.find((item) => item.id === fieldName)?.default
      if (!isObjectDefault(defaultValue)) {
        throw new Error(`${backendType}.${fieldName} must declare an object creation default`)
      }
      normalized[fieldName] = { ...defaultValue }
    }
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined
}
